import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';
import { useParams, useNavigate } from 'react-router-dom';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';

const API = process.env.REACT_APP_API_URL || 'https://agents.matix.one';

// Contract data placeholders the user can insert
const CONTRACT_FIELDS = [
  { id: 'contract_number', label: 'Contract Number', label_ar: 'رقم العقد' },
  { id: 'title', label: 'Contract Title', label_ar: 'عنوان العقد' },
  { id: 'title_ar', label: 'Contract Title (AR)', label_ar: 'عنوان العقد (عربي)' },
  { id: 'contract_type', label: 'Contract Type', label_ar: 'نوع العقد' },
  { id: 'status', label: 'Status', label_ar: 'الحالة' },
  { id: 'start_date', label: 'Start Date', label_ar: 'تاريخ البداية' },
  { id: 'end_date', label: 'End Date', label_ar: 'تاريخ النهاية' },
  { id: 'contract_value', label: 'Contract Value', label_ar: 'قيمة العقد' },
  { id: 'currency', label: 'Currency', label_ar: 'العملة' },
  { id: 'visit_frequency', label: 'Visit Frequency', label_ar: 'تكرار الزيارات' },
  { id: 'total_visits_allowed', label: 'Total Visits', label_ar: 'إجمالي الزيارات' },
  { id: 'coverage_scope', label: 'Coverage Scope', label_ar: 'نطاق التغطية' },
  { id: 'payment_terms', label: 'Payment Terms', label_ar: 'شروط الدفع' },
  { id: 'sla_response_hours', label: 'SLA Response (hrs)', label_ar: 'وقت الاستجابة' },
  { id: 'sla_resolution_hours', label: 'SLA Resolution (hrs)', label_ar: 'وقت الحل' },
  { id: 'sla_uptime_percent', label: 'Uptime %', label_ar: 'ضمان التشغيل' },
  { id: 'terms_conditions', label: 'Terms & Conditions', label_ar: 'الشروط والأحكام' },
  { id: 'notes', label: 'Notes', label_ar: 'ملاحظات' },
  { id: 'client_name', label: 'Client Name', label_ar: 'اسم العميل' },
  { id: 'client_name_ar', label: 'Client Name (AR)', label_ar: 'اسم العميل (عربي)' },
  { id: 'client_contact', label: 'Contact Person', label_ar: 'جهة الاتصال' },
  { id: 'client_phone', label: 'Client Phone', label_ar: 'هاتف العميل' },
  { id: 'client_email', label: 'Client Email', label_ar: 'بريد العميل' },
  { id: 'client_address', label: 'Client Address', label_ar: 'عنوان العميل' },
  { id: 'org_name', label: 'Organization', label_ar: 'المنظمة' },
  { id: 'today_date', label: "Today's Date", label_ar: 'تاريخ اليوم' },
  { id: 'equipment_table', label: 'Equipment Table', label_ar: 'جدول المعدات' },
];

// Default contract template HTML
const DEFAULT_TEMPLATE = `
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Cairo', 'Segoe UI', Tahoma, Arial, sans-serif; font-size: 12pt; color: #333; line-height: 1.6; }
  .contract-page { max-width: 210mm; margin: 0 auto; padding: 20mm; background: white; }
  .header { text-align: center; margin-bottom: 20px; }
  .header h1 { font-size: 22pt; color: #1a2038; margin-bottom: 5px; }
  .header h3 { font-size: 12pt; color: #007bff; font-weight: normal; }
  .header .contract-no { font-size: 10pt; color: #888; }
  .divider { border: none; border-top: 2px solid #1a2038; margin: 15px 0; }
  .divider-light { border: none; border-top: 1px solid #ddd; margin: 15px 0; }
  .section-title { font-size: 14pt; font-weight: bold; color: #1a2038; margin: 15px 0 10px; }
  .field-row { display: flex; margin-bottom: 6px; }
  .field-label { width: 180px; color: #888; font-size: 10pt; flex-shrink: 0; }
  .field-value { font-weight: bold; font-size: 10pt; }
  .two-col { display: flex; gap: 20px; }
  .two-col > div { flex: 1; }
  .info-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
  .info-table th { background: #1a2038; color: white; padding: 8px 12px; text-align: left; font-size: 9pt; }
  .info-table td { padding: 8px 12px; border: 1px solid #ddd; font-size: 9pt; }
  .info-table tr:nth-child(even) { background: #f9f9f9; }
  .terms-text { font-size: 10pt; line-height: 1.8; text-align: justify; }
  .signatures { display: flex; justify-content: space-between; margin-top: 40px; }
  .sig-block { text-align: center; min-width: 200px; }
  .sig-line { border-bottom: 1px solid #333; width: 200px; margin: 0 auto 8px; height: 40px; }
  .sig-label { font-weight: bold; font-size: 10pt; }
  .sig-date { color: #888; font-size: 9pt; margin-top: 4px; }
  .footer { text-align: center; font-size: 8pt; color: #999; margin-top: 30px; }
  .placeholder { background: #e8f0fe; border: 1px dashed #4285f4; padding: 2px 6px; border-radius: 3px; color: #1a73e8; font-size: inherit; }
  @media print {
    .contract-page { padding: 15mm; max-width: none; }
    body { -webkit-print-color-adjust: exact; }
  }
</style>

<div class="contract-page">
  <div class="header">
    <h1>{{org_name}}</h1>
    <h3>SERVICE MAINTENANCE CONTRACT</h3>
    <div class="contract-no">{{contract_number}}</div>
  </div>

  <hr class="divider" />

  <div class="section-title">1. Parties</div>
  <div class="two-col">
    <div>
      <div class="field-row"><span class="field-label">Service Provider:</span> <span class="field-value">{{org_name}}</span></div>
    </div>
    <div>
      <div class="field-row"><span class="field-label">Client:</span> <span class="field-value">{{client_name}}</span></div>
      <div class="field-row"><span class="field-label">Contact:</span> <span class="field-value">{{client_contact}}</span></div>
      <div class="field-row"><span class="field-label">Phone:</span> <span class="field-value">{{client_phone}}</span></div>
      <div class="field-row"><span class="field-label">Email:</span> <span class="field-value">{{client_email}}</span></div>
      <div class="field-row"><span class="field-label">Address:</span> <span class="field-value">{{client_address}}</span></div>
    </div>
  </div>

  <hr class="divider-light" />

  <div class="section-title">2. Contract Details</div>
  <div class="two-col">
    <div>
      <div class="field-row"><span class="field-label">Contract Type:</span> <span class="field-value">{{contract_type}}</span></div>
      <div class="field-row"><span class="field-label">Start Date:</span> <span class="field-value">{{start_date}}</span></div>
      <div class="field-row"><span class="field-label">Coverage:</span> <span class="field-value">{{coverage_scope}}</span></div>
      <div class="field-row"><span class="field-label">Total Visits:</span> <span class="field-value">{{total_visits_allowed}}</span></div>
    </div>
    <div>
      <div class="field-row"><span class="field-label">Status:</span> <span class="field-value">{{status}}</span></div>
      <div class="field-row"><span class="field-label">End Date:</span> <span class="field-value">{{end_date}}</span></div>
      <div class="field-row"><span class="field-label">Visit Frequency:</span> <span class="field-value">{{visit_frequency}}</span></div>
    </div>
  </div>

  <hr class="divider-light" />

  <div class="section-title">3. Service Level Agreement (SLA)</div>
  <table class="info-table">
    <tr><th>SLA Metric</th><th>Target</th></tr>
    <tr><td>Response Time</td><td>{{sla_response_hours}} hours</td></tr>
    <tr><td>Resolution Time</td><td>{{sla_resolution_hours}} hours</td></tr>
    <tr><td>Uptime Guarantee</td><td>{{sla_uptime_percent}}%</td></tr>
  </table>

  <hr class="divider-light" />

  <div class="section-title">4. Financial Terms</div>
  <div class="two-col">
    <div>
      <div class="field-row"><span class="field-label">Contract Value:</span> <span class="field-value" style="color:#007bff;">{{contract_value}} {{currency}}</span></div>
    </div>
    <div>
      <div class="field-row"><span class="field-label">Payment Terms:</span> <span class="field-value">{{payment_terms}}</span></div>
    </div>
  </div>

  <hr class="divider-light" />

  <div class="section-title">5. Covered Equipment</div>
  <p style="font-size:10pt; margin-bottom:8px;">The following equipment is covered under this contract:</p>
  {{equipment_table}}

  <hr class="divider-light" />

  <div class="section-title">6. Terms & Conditions</div>
  <div class="terms-text">{{terms_conditions}}</div>

  <div style="margin-top:10px;">
    <strong>Notes:</strong> {{notes}}
  </div>

  <div class="signatures">
    <div class="sig-block">
      <div class="sig-line"></div>
      <div class="sig-label">Client Representative</div>
      <div class="sig-date">Date: _______________</div>
    </div>
    <div class="sig-block">
      <div class="sig-line"></div>
      <div class="sig-label">Service Provider</div>
      <div class="sig-date">Date: _______________</div>
    </div>
  </div>

  <div class="footer">
    Generated on {{today_date}} | Contract Reference: {{contract_number}}
  </div>
</div>
`;

const ContractTemplateBuilder = () => {
  const { lang, t } = useLanguage();
  const isRTL = lang === 'ar';
  const { id } = useParams();
  const navigate = useNavigate();

  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const [templateMeta, setTemplateMeta] = useState({
    name: '', name_ar: '', language: 'en', is_default: false
  });
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  const inputSt = {
    width: '100%', padding: '10px',
    border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', backgroundColor: '#1a2038',
    color: '#f9fafb', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
  };
  const labelSt = { display: 'block', marginBottom: '6px', color: '#d1d5db', fontSize: '12px', fontWeight: '500' };

  useEffect(() => {
    let destroyed = false;

    const init = async () => {
      await new Promise(r => setTimeout(r, 300));
      if (destroyed || !containerRef.current) return;

      let htmlContent = DEFAULT_TEMPLATE;
      let cssContent = '';

      // Load existing template if editing
      if (id) {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API}/api/v1/fsm/contract-templates/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json();
          if (data.success && data.data) {
            const tmpl = data.data;
            setTemplateMeta({
              name: tmpl.name || '', name_ar: tmpl.name_ar || '',
              language: tmpl.language || 'en', is_default: !!tmpl.is_default
            });

            const stored = typeof tmpl.schema === 'string' ? JSON.parse(tmpl.schema) : tmpl.schema;
            if (stored && stored.html) {
              htmlContent = stored.html;
              cssContent = stored.css || '';
            } else if (stored && typeof stored === 'string') {
              htmlContent = stored;
            }
          }
        } catch (err) {
          console.error('Error loading template:', err);
        }
      }

      if (destroyed || !containerRef.current) return;

      // Destroy old editor
      if (editorRef.current) {
        try { editorRef.current.destroy(); } catch (e) {}
      }

      // Initialize GrapesJS
      const editor = grapesjs.init({
      assetManager: {
        upload: false,
        custom: {
          open(props) {
            // Create a hidden file input
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.multiple = true;
            input.onchange = async (e) => {
              const files = e.target.files;
              if (!files || files.length === 0) return;
              const token = localStorage.getItem('token');
              for (let i = 0; i < files.length; i++) {
                const formData = new FormData();
                formData.append('file', files[i]);
                try {
                  const res = await fetch('/api/v1/fsm/contract-templates/upload-asset', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData,
                  });
                  const data = await res.json();
                  if (data.success && data.url) {
                    props.select({ src: data.url, type: 'image' });
                  }
                } catch (err) {
                  console.error('Asset upload failed:', err);
                }
              }
            };
            input.click();
          },
          close() {},
        },
      },
        container: containerRef.current,
        height: '100%',
        width: 'auto',
        fromElement: false,
        components: htmlContent,
        style: cssContent,
        storageManager: false,
        panels: { defaults: [] },
        deviceManager: {
          devices: [
            { name: 'A4 Portrait', width: '210mm' },
            { name: 'A4 Landscape', width: '297mm' },
            { name: 'Letter', width: '8.5in' },
          ]
        },
        canvas: {
          styles: [
            'https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Amiri:wght@400;700&display=swap',
          ],
        },
        plugins: [],
        pluginsOpts: {},
      });

      // Add custom blocks for contract fields
      const bm = editor.BlockManager;

      // Section blocks
      bm.add('section-heading', {
        label: '📋 Section Title',
        category: 'Layout',
        content: '<div class="section-title">Section Title</div>',
      });

      bm.add('divider', {
        label: '➖ Divider',
        category: 'Layout',
        content: '<hr class="divider-light" />',
      });

      bm.add('divider-bold', {
        label: '➖ Bold Divider',
        category: 'Layout',
        content: '<hr class="divider" />',
      });

      bm.add('two-columns', {
        label: '📐 Two Columns',
        category: 'Layout',
        content: '<div class="two-col"><div><p>Column 1</p></div><div><p>Column 2</p></div></div>',
      });

      bm.add('three-columns', {
        label: '📐 Three Columns',
        category: 'Layout',
        content: '<div style="display:flex;gap:15px;"><div style="flex:1;"><p>Col 1</p></div><div style="flex:1;"><p>Col 2</p></div><div style="flex:1;"><p>Col 3</p></div></div>',
      });

      bm.add('text-block', {
        label: '📝 Text Block',
        category: 'Content',
        content: '<p class="terms-text">Enter your text here. You can use {{field_name}} placeholders for dynamic contract data.</p>',
      });

      bm.add('heading', {
        label: '📰 Heading',
        category: 'Content',
        content: '<h2 style="font-size:18pt; color:#1a2038; margin:10px 0;">Heading Text</h2>',
      });

      bm.add('image-block', {
        label: '🖼️ Image/Logo',
        category: 'Content',
        content: '<div style="text-align:center; margin:10px 0;"><img src="https://via.placeholder.com/200x80?text=Your+Logo" style="max-width:200px; max-height:80px;" /></div>',
      });

      bm.add('spacer', {
        label: '↕️ Spacer',
        category: 'Layout',
        content: '<div style="height:20px;"></div>',
      });

      // Signature block
      bm.add('signatures', {
        label: '✒️ Signatures',
        category: 'Content',
        content: `<div class="signatures">
          <div class="sig-block"><div class="sig-line"></div><div class="sig-label">Signature 1</div><div class="sig-date">Date: _______________</div></div>
          <div class="sig-block"><div class="sig-line"></div><div class="sig-label">Signature 2</div><div class="sig-date">Date: _______________</div></div>
        </div>`,
      });

      // Data table
      bm.add('data-table', {
        label: '📊 Data Table',
        category: 'Content',
        content: `<table class="info-table">
          <tr><th>Column 1</th><th>Column 2</th><th>Column 3</th></tr>
          <tr><td>Data</td><td>Data</td><td>Data</td></tr>
          <tr><td>Data</td><td>Data</td><td>Data</td></tr>
        </table>`,
      });

      // Field row (label + value)
      bm.add('field-row', {
        label: '📋 Field Row',
        category: 'Content',
        content: '<div class="field-row"><span class="field-label">Label:</span> <span class="field-value">{{field_name}}</span></div>',
      });

      // Contract field placeholder blocks
      CONTRACT_FIELDS.forEach(field => {
        if (field.id === 'equipment_table') {
          bm.add(`field-${field.id}`, {
            label: `⚙️ ${field.label}`,
            category: 'Contract Fields',
            content: `<table class="info-table">
              <tr><th>#</th><th>Equipment</th><th>Serial #</th><th>Model</th><th>Brand</th><th>Category</th></tr>
              <tr><td>1</td><td>{{equipment_name}}</td><td>{{serial}}</td><td>{{model}}</td><td>{{brand}}</td><td>{{category}}</td></tr>
            </table>`,
          });
        } else {
          bm.add(`field-${field.id}`, {
            label: `📌 ${field.label}`,
            category: 'Contract Fields',
            content: `<span class="placeholder">{{${field.id}}}</span>`,
          });
        }
      });

      // Add custom panels
      const pn = editor.Panels;

      // Toolbar buttons
      pn.addPanel({
        id: 'views',
        buttons: [
          { id: 'open-blocks', active: true, label: 'Blocks', command: 'open-blocks', togglable: false },
          { id: 'open-sm', label: 'Styles', command: 'open-sm', togglable: false },
          { id: 'open-layers', label: 'Layers', command: 'open-layers', togglable: false },
        ],
      });

      editorRef.current = editor;
      setReady(true);
      console.log('GrapesJS editor initialized');
    };

    init();

    return () => {
      destroyed = true;
      if (editorRef.current) {
        try { editorRef.current.destroy(); } catch (e) {}
      }
    };
  }, [id]);

  // Save template
  const handleSave = async () => {
    if (!templateMeta.name) {
      alert('Template name is required');
      return;
    }
    if (!editorRef.current) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const editor = editorRef.current;

      const schema = {
        html: editor.getHtml(),
        css: editor.getCss(),
        components: JSON.stringify(editor.getComponents()),
        styles: JSON.stringify(editor.getStyle()),
      };

      const payload = {
        name: templateMeta.name,
        name_ar: templateMeta.name_ar,
        language: templateMeta.language,
        is_default: templateMeta.is_default,
        schema,
      };

      const url = id
        ? `${API}/api/v1/fsm/contract-templates/${id}`
        : `${API}/api/v1/fsm/contract-templates`;

      const res = await fetch(url, {
        method: id ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        if (!id && data.data && data.data.id) {
          navigate(`/fsm/contract-templates/${data.data.id}/edit`, { replace: true });
        }
        alert('Template saved!');
      } else {
        alert(data.message || 'Error saving');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Preview / Print PDF
  const handlePreviewPDF = () => {
    if (!editorRef.current) return;

    const editor = editorRef.current;
    const html = editor.getHtml();
    const css = editor.getCss();

    const fullHTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Amiri:wght@400;700&display=swap" rel="stylesheet">
  <style>
    ${css}
    @page { size: A4; margin: 0; }
    @media print { body { -webkit-print-color-adjust: exact; } }
  </style>
</head>
<body>${html}</body>
</html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(fullHTML);
    printWindow.document.close();
    // Auto print after fonts load
    printWindow.onload = () => {
      setTimeout(() => printWindow.print(), 500);
    };
  };

  return (
    <Layout>
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', color: '#f9fafb', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)' }}>

        {/* Top Bar */}
        <div style={{
          display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap',
          padding: '12px 16px',
          backgroundColor: 'rgba(26, 32, 56, 0.9)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          zIndex: 10
        }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label style={labelSt}>Template Name *</label>
            <input type="text" value={templateMeta.name}
              onChange={(e) => setTemplateMeta(prev => ({ ...prev, name: e.target.value }))}
              style={inputSt} placeholder="e.g. Standard Maintenance Contract" />
          </div>
          <div style={{ minWidth: '100px' }}>
            <label style={labelSt}>Name (AR)</label>
            <input type="text" value={templateMeta.name_ar}
              onChange={(e) => setTemplateMeta(prev => ({ ...prev, name_ar: e.target.value }))}
              style={{ ...inputSt, direction: 'rtl' }} />
          </div>
          <div style={{ minWidth: '100px' }}>
            <label style={labelSt}>Language</label>
            <select value={templateMeta.language}
              onChange={(e) => setTemplateMeta(prev => ({ ...prev, language: e.target.value }))}
              style={inputSt}>
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#d1d5db', fontSize: '12px', paddingBottom: '10px' }}>
            <input type="checkbox" checked={templateMeta.is_default}
              onChange={(e) => setTemplateMeta(prev => ({ ...prev, is_default: e.target.checked }))}
              style={{ accentColor: '#007bff' }} />
            Default
          </label>
          <div style={{ display: 'flex', gap: '8px', paddingBottom: '2px' }}>
            <button onClick={handleSave} disabled={saving}
              style={{
                backgroundColor: saving ? '#6c757d' : '#007bff', color: 'white', border: 'none',
                borderRadius: '8px', padding: '10px 20px', cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: '600'
              }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={handlePreviewPDF}
              style={{
                backgroundColor: 'transparent', color: '#4ade80',
                border: '2px solid rgba(74,222,128,0.4)', borderRadius: '8px',
                padding: '10px 16px', cursor: 'pointer', fontSize: '14px'
              }}>
              Preview / Print PDF
            </button>
            <button onClick={() => navigate('/fsm/contract-templates')}
              style={{
                backgroundColor: 'transparent', color: '#d1d5db',
                border: '2px solid rgba(255,255,255,0.2)', borderRadius: '8px',
                padding: '10px 16px', cursor: 'pointer', fontSize: '14px'
              }}>
              Back
            </button>
          </div>
        </div>

        {/* GrapesJS Editor Container */}
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {!ready && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(21,27,48,0.9)', zIndex: 10, color: '#9ca3af' }}>
              Loading editor...
            </div>
          )}
          <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        </div>

      </div>

      {/* Override GrapesJS styles for dark theme compatibility */}
      <style>{`
        .gjs-one-bg { background-color: #1a2038 !important; }
        .gjs-two-color { color: #d1d5db !important; }
        .gjs-three-bg { background-color: #151b30 !important; }
        .gjs-four-color, .gjs-four-color-h:hover { color: #60a5fa !important; }
        .gjs-pn-views, .gjs-pn-views-container { background-color: #1a2038 !important; }
        .gjs-block { background-color: rgba(255,255,255,0.05) !important; border: 1px solid rgba(255,255,255,0.1) !important; border-radius: 8px !important; color: #d1d5db !important; min-height: auto !important; }
        .gjs-block:hover { border-color: rgba(0,123,255,0.5) !important; }
        .gjs-block__media { display: none !important; }
        .gjs-blocks-cs { padding: 5px !important; }
        .gjs-category-title, .gjs-layer-title, .gjs-sm-sector-title { background-color: rgba(255,255,255,0.05) !important; border-color: rgba(255,255,255,0.1) !important; color: #d1d5db !important; }
        .gjs-sm-label, .gjs-field { color: #d1d5db !important; }
        .gjs-field input, .gjs-field select, .gjs-field textarea { color: #f9fafb !important; background: #1a2038 !important; }
        .gjs-pn-btn { color: #9ca3af !important; }
        .gjs-pn-btn.gjs-pn-active { color: #60a5fa !important; }
        .gjs-cv-canvas { background-color: #2a2f45 !important; }
      `}</style>
    </Layout>
  );
};

export default ContractTemplateBuilder;